import { t } from 'i18next'

import type { TSimulacaoProposta } from '@/features/proposta/schemas'
import { formatDateToPtBr } from '@/shared/utils/date.utils'

export interface ResumoSimulacao {
  nomeEmpreendimento: string
  blocoEmpreendimento: string
  dataMarcacaoAlvenaria: string
  valorProposta: string
  valorPoupanca: string
  valorSaldo: string
  entrada: string[]
  mensais: string[]
  intermediarias: string[]
  intermediariasPosConclusao: string[]
  posConclusao: string[]
  codigoProdutoPrincipal: string
  codigosAcessorios: string
  tipoDeProduto?: TSimulacaoProposta['produto']['tipoDoProduto']
  tipoDeAmortizacao?: string
  formaDePagamento?: string
  labelBloco: string
  labelProduto: string
}

interface SerieAgg {
  quantidade: number
  valor: number
}

export class ResumoSimulacoes {
  static from(
    simulacaoUnidade?: TSimulacaoProposta | null,
    simulacaoLote?: TSimulacaoProposta | null,
    simulacoesAcessorios: TSimulacaoProposta[] = [],
  ): ResumoSimulacao {
    const simulacoes = [simulacaoUnidade, simulacaoLote, ...simulacoesAcessorios].filter(
      (simulacao): simulacao is TSimulacaoProposta => !!simulacao,
    )
    if (!simulacoes.length) return ResumoSimulacoes.emptyResumo()
    return ResumoSimulacoes.buildResumo(simulacoes)
  }

  private static buildResumo(simulacoes: TSimulacaoProposta[]): ResumoSimulacao {
    const primeiraSimulacao = simulacoes[0]!

    const aggEntrada: SerieAgg[] = []
    const aggMensais: SerieAgg[] = []
    const aggIntermediarias: SerieAgg[] = []
    const aggIntermediariasPosConclusao: SerieAgg[] = []
    const aggPosConclusao: SerieAgg[] = []

    let totalProposta = 0
    let totalPoupanca = 0
    let totalSaldo = 0

    for (const simulacao of simulacoes) {
      totalProposta += simulacao.valorTotalSimulado ?? 0
      totalPoupanca += simulacao.valorPoupanca ?? 0
      totalSaldo += simulacao.valorSaldo ?? 0

      simulacao.entrada.forEach((p) => this.addToAgg(aggEntrada, p.valor))
      simulacao.mensais.forEach((s) => s.parcelas.forEach((p) => this.addToAgg(aggMensais, p.valor)))
      simulacao.intermediarias.forEach((s) => s.parcelas.forEach((p) => this.addToAgg(aggIntermediarias, p.valor)))
      simulacao.intermediariasPosConclusao?.forEach((s) =>
        s.parcelas.forEach((p) => this.addToAgg(aggIntermediariasPosConclusao, p.valor)),
      )
      simulacao.posConclusao.forEach((p) => this.addToAgg(aggPosConclusao, p.valor))
    }

    const produtosPrincipais = ['Unidade', 'Lote']
    const simulacaoProdutoPrincipal = simulacoes.find((simulacao) =>
      produtosPrincipais.includes(simulacao.produto.tipoDoProduto),
    )
    const codigoProdutoPrincipal = simulacaoProdutoPrincipal?.produto.codigo ?? '-'
    const acessorios = simulacoes.filter((s) => s.produto.tipoDoProduto === 'Acessorio').map((s) => s.produto.codigo)
    const codigosAcessorios = acessorios.length ? acessorios.join(', ') : '-'

    const tipoDeProduto = primeiraSimulacao.produto.tipoDoProduto
    const { labelBloco, labelProduto } = this.getLabels(tipoDeProduto)

    const simulacaoComData = simulacoes.find(
      (simulacao) => simulacao.produto.tipoDoProduto === 'Unidade' && simulacao.produto.dataMarcacaoAlvenaria,
    )
    const dataMarcacaoAlvenaria = simulacaoComData?.produto.dataMarcacaoAlvenaria
      ? formatDateToPtBr(simulacaoComData.produto.dataMarcacaoAlvenaria)
      : '-'

    return {
      nomeEmpreendimento: primeiraSimulacao.empreendimento.nome,
      blocoEmpreendimento: primeiraSimulacao.bloco.nome,
      dataMarcacaoAlvenaria,
      valorProposta: this.formatMoney(totalProposta),
      valorPoupanca: this.formatMoney(totalPoupanca),
      valorSaldo: this.formatMoney(totalSaldo),
      entrada: this.formatSeries(aggEntrada),
      mensais: this.formatSeries(aggMensais),
      intermediarias: this.formatSeries(aggIntermediarias),
      intermediariasPosConclusao: this.formatSeries(aggIntermediariasPosConclusao),
      posConclusao: this.formatSeries(aggPosConclusao),
      codigoProdutoPrincipal,
      codigosAcessorios,
      tipoDeProduto,
      tipoDeAmortizacao: primeiraSimulacao.tipoDeAmortizacao ?? undefined,
      formaDePagamento: primeiraSimulacao.formaDePagamento ?? undefined,
      labelBloco,
      labelProduto,
    }
  }

  private static getLabels(tipoProduto: TSimulacaoProposta['produto']['tipoDoProduto']): {
    labelBloco: string
    labelProduto: string
  } {
    if (tipoProduto === 'Lote') {
      return {
        labelBloco: t('proposta:resumo_proposta.proposta_compra.label.quadra'),
        labelProduto: t('proposta:resumo_proposta.proposta_compra.label.lote'),
      }
    }

    return {
      labelBloco: t('proposta:resumo_proposta.proposta_compra.label.torre'),
      labelProduto: t('proposta:resumo_proposta.proposta_compra.label.unidade'),
    }
  }

  private static addToAgg(agg: SerieAgg[], valor: number): void {
    const linha = agg.find((l) => l.valor === valor)
    if (linha) {
      linha.quantidade += 1
      return
    }
    agg.push({ quantidade: 1, valor })
  }

  private static formatSeries(agg: SerieAgg[]): string[] {
    if (!agg.length) return ['R$ 0,00']
    return agg.map((l) => `${l.quantidade}x ${this.formatMoney(l.valor)}`)
  }

  private static formatMoney(n: number): string {
    return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
  }

  private static emptyResumo(): ResumoSimulacao {
    return {
      nomeEmpreendimento: '-',
      blocoEmpreendimento: '-',
      dataMarcacaoAlvenaria: '-',
      valorProposta: '-',
      valorPoupanca: '-',
      valorSaldo: '-',
      entrada: [],
      mensais: [],
      intermediarias: [],
      intermediariasPosConclusao: [],
      posConclusao: [],
      codigoProdutoPrincipal: '-',
      codigosAcessorios: '-',
      tipoDeAmortizacao: undefined,
      formaDePagamento: undefined,
      labelBloco: 'Torre',
      labelProduto: 'Unidade',
    }
  }
}
